/**
 * useCall — orquestra ciclo de vida de uma chamada áudio/vídeo (LiveKit).
 *
 * Wrappa endpoints `POST /api/v1/calls/*` (Story 3.2.1) e expõe state machine
 * `idle → connecting → ending → ended | error` para o `CallProvider`
 * (`contexts/CallContext.tsx`) consumir. (`connected` segue no tipo `CallStatus`, mas nada neste
 * hook o grava: quem o gravava era o ciclo de reconexão, que saiu.)
 *
 * **Não conecta ao LiveKit aqui** — quem conecta é o `<LiveKitRoom>` mounted
 * pelo `<InlineCallRoom>` usando `token` + `wsUrl` retornados por `initiate()` /
 * `join()`. Este hook é puramente o "controller" da API REST.
 *
 * **Sem reconexão aqui:** o ciclo de reconexão da Story 3.2.4 nunca teve chamador e foi apagado.
 * A reconciliação da ligação que ficou para trás (sino caído, fim perdido) mora no `CallContext`.
 *
 * Refs:
 * - Story 3.2.2: docs/MODULO_EXPANSAO_2026/stories/individual/3.2.2.md
 * - Story 3.2.1 (endpoints): V1/backend/app/api/v1/endpoints/calls.py
 * - ARCH-PRD-3 ADR-304 (recommended_mode), ADR-306 (TURN HMAC)
 */

import { useCallback, useRef, useState } from "react";
import type { AxiosError } from "axios";

import apiClient from "@/lib/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CallMode = "P2P_MESH" | "SFU_LIVE" | "SFU_RECORDED";

export type CallStatus = "idle" | "connecting" | "connected" | "ending" | "ended" | "error";

export type EndReason = "normal" | "forced" | "timeout" | "error" | "drained";

export type CallMediaType = "audio" | "video" | "screen";

export type TurnCredentials = {
  username: string;
  credential: string;
  urls: string[];
  ttl: number;
  expiresAt: number;
};

export type InitiateArgs = {
  targetUserId?: string | null;
  targetRoomId?: string | null;
  ticketId?: string | null;
  customerSessionId?: string | null;
  mediaTypes: CallMediaType[];
  participantsCount?: number;
  recordingRequired?: boolean;
  maxParticipants?: number;
};

export type CallSnapshot = {
  callSessionId: string;
  roomName: string;
  mode: CallMode;
  token: string;
  wsUrl: string;
  turnCredentials: TurnCredentials;
};

/**
 * O que o `initiate` devolve além de gravar o estado: o que o `CallProvider` precisa saber da
 * ligação do chat que acabou de começar (o id da sessão, a sala onde ela acontece, quantas pessoas
 * estão tocando e quando o toque acaba). O id vem aqui, e não só no estado do hook, porque o
 * `chamada:atendida` de quem liga pode chegar ANTES do render que mostra o estado. Servidor antigo,
 * sem os campos da sala: `null`, `0` e `null`.
 */
export type InitiateResult = {
  callSessionId: string;
  roomId: string | null;
  tocaPara: number;
  expiraEm: string | null;
};

export type UseCallResult = {
  status: CallStatus;
  callSessionId: string | null;
  roomName: string | null;
  token: string | null;
  wsUrl: string | null;
  turnCredentials: TurnCredentials | null;
  mode: CallMode | null;
  error: string | null;
  /**
   * Inicia a ligação. Grava o estado (`connecting`, ou `error` + `error` com o detalhe) e, na
   * falha, **relança** o erro original do axios: o `CallProvider` precisa ler o 409
   * (`em_outra_ligacao`) e o 422 do servidor para mostrar a mensagem certa.
   */
  initiate: (args: InitiateArgs) => Promise<InitiateResult>;
  /**
   * Entra numa ligação (atender, ou "Entrar" no grupo). Mesmo contrato do `initiate`: grava o
   * estado e **relança** o erro (o 409 "A ligação já terminou." é lido por quem chamou).
   */
  join: (callSessionId: string) => Promise<void>;
  leave: () => Promise<void>;
  end: (reason?: EndReason) => Promise<void>;
};

// ---------------------------------------------------------------------------
// Snake_case → camelCase boundary helpers
// ---------------------------------------------------------------------------

type RawTurnCredentials = {
  username: string;
  credential: string;
  urls: string[];
  ttl: number;
  expires_at: number;
};

type RawInitiateResponse = {
  call_session_id: string;
  room_name: string;
  mode: CallMode;
  token: string;
  ws_url: string;
  turn_credentials: RawTurnCredentials;
  /** Só o `/initiate` do chat traz estes três (servidor antigo: ausentes). */
  room_id?: string | null;
  toca_para?: number;
  expira_em?: string | null;
};

type RawJoinResponse = RawInitiateResponse;

function toCamelTurn(t: RawTurnCredentials): TurnCredentials {
  return {
    username: t.username,
    credential: t.credential,
    urls: t.urls,
    ttl: t.ttl,
    expiresAt: t.expires_at,
  };
}

function toCamelInitiate(d: RawInitiateResponse): CallSnapshot {
  return {
    callSessionId: d.call_session_id,
    roomName: d.room_name,
    mode: d.mode,
    token: d.token,
    wsUrl: d.ws_url,
    turnCredentials: toCamelTurn(d.turn_credentials),
  };
}

/**
 * O texto do erro do servidor. `detail` é texto na maioria das rotas; o 409 do `/initiate`
 * (`em_outra_ligacao`) manda `{ error, mensagem }` e o 422 de validação do FastAPI manda uma lista.
 * Texto vira ele mesmo; objeto com `mensagem` em texto vira a `mensagem`; o resto, o `fallback`
 * (o `state.error` é sempre texto, nunca um objeto).
 */
function extractDetail(err: unknown, fallback: string): string {
  const axiosErr = err as AxiosError<{ detail?: unknown }>;
  const detail = axiosErr.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const mensagem = (detail as { mensagem?: unknown }).mensagem;
    if (typeof mensagem === "string" && mensagem) return mensagem;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

type InternalState = Omit<UseCallResult, "initiate" | "join" | "leave" | "end">;

const INITIAL_STATE: InternalState = {
  status: "idle",
  callSessionId: null,
  roomName: null,
  token: null,
  wsUrl: null,
  turnCredentials: null,
  mode: null,
  error: null,
};

export function useCall(): UseCallResult {
  const [state, setState] = useState<InternalState>(INITIAL_STATE);

  // Session id mirror via ref to avoid stale-closure bugs: handlers
  // (leave/end) are memoized via useCallback and would
  // otherwise capture an outdated `state.callSessionId` if the user calls
  // them in the same render-frame as `initiate()` resolved. `useRef.current`
  // is read at call time, not at memoization time.
  const sessionIdRef = useRef<string | null>(null);

  const applySnapshot = useCallback((snap: CallSnapshot) => {
    sessionIdRef.current = snap.callSessionId;
    setState({
      status: "connecting",
      callSessionId: snap.callSessionId,
      roomName: snap.roomName,
      mode: snap.mode,
      token: snap.token,
      wsUrl: snap.wsUrl,
      turnCredentials: snap.turnCredentials,
      error: null,
    });
  }, []);

  const initiate = useCallback(
    async (args: InitiateArgs) => {
      const body = {
        target_user_id: args.targetUserId ?? null,
        target_room_id: args.targetRoomId ?? null,
        ticket_id: args.ticketId ?? null,
        customer_session_id: args.customerSessionId ?? null,
        media_types: args.mediaTypes,
        participants_count: args.participantsCount ?? 2,
        recording_required: args.recordingRequired ?? false,
        max_participants: args.maxParticipants ?? 50,
      };
      try {
        const res = await apiClient.post<RawInitiateResponse>("/calls/initiate", body);
        applySnapshot(toCamelInitiate(res.data));
        return {
          callSessionId: res.data.call_session_id,
          roomId: res.data.room_id ?? null,
          tocaPara: res.data.toca_para ?? 0,
          expiraEm: res.data.expira_em ?? null,
        };
      } catch (err) {
        const detail = extractDetail(err, "Falha ao iniciar chamada");

        console.error("[useCall] initiate failed:", detail);
        setState((prev) => ({ ...prev, status: "error", error: detail }));
        // O estado já registra a falha; quem chamou lê o erro (409/422) para dizer o motivo.
        throw err;
      }
    },
    [applySnapshot]
  );

  const join = useCallback(
    async (callSessionId: string) => {
      try {
        const res = await apiClient.post<RawJoinResponse>(`/calls/${callSessionId}/join`, {});
        applySnapshot(toCamelInitiate(res.data));
      } catch (err) {
        const detail = extractDetail(err, "Falha ao entrar na chamada");

        console.error("[useCall] join failed:", detail);
        setState((prev) => ({ ...prev, status: "error", error: detail }));
        // Idem `initiate`: o 409 "A ligação já terminou." é lido por quem chamou.
        throw err;
      }
    },
    [applySnapshot]
  );

  const leave = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return; // No-op if never initiated/joined — avoids 404.
    try {
      await apiClient.post(`/calls/${sessionId}/leave`);
      setState((prev) => ({
        ...prev,
        status: "ended",
        token: null,
        wsUrl: null,
        turnCredentials: null,
      }));
    } catch (err) {
      const detail = extractDetail(err, "Falha ao sair da chamada");

      console.error("[useCall] leave failed:", detail);
      setState((prev) => ({ ...prev, status: "error", error: detail }));
    }
  }, []);

  const end = useCallback(async (reason: EndReason = "normal") => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    setState((prev) => ({ ...prev, status: "ending" }));
    try {
      await apiClient.post(`/calls/${sessionId}/end`, { reason });
      setState((prev) => ({
        ...prev,
        status: "ended",
        token: null,
        wsUrl: null,
        turnCredentials: null,
      }));
    } catch (err) {
      const detail = extractDetail(err, "Falha ao encerrar a chamada");

      console.error("[useCall] end failed:", detail);
      setState((prev) => ({ ...prev, status: "error", error: detail }));
    }
  }, []);

  return {
    ...state,
    initiate,
    join,
    leave,
    end,
  };
}
